import './styles.css';
import { getHitPolicy, HIT_POLICIES, AGGREGATIONS, getHitPolicyLetter } from './hitPolicies';
import { FEEL_TYPES } from '../widgets/feelTypes';
import { makeFeelEditable } from '../expression/feelField';
import { computeScope } from '../scope/computeScope';
import { LOGIC_TYPES } from '../expression/shared';
import { ScopePanel } from '../scope/ScopePanel';
import { sanitizeFeelIdent } from '../feel/ident';

// Plain-DOM Decision Table editor.
//
// Mutates the moddle `decisionTable` element in place so that the surrounding
// `dmn:Definitions` tree stays canonical and round-trips without loss.
// The DOM is rebuilt on every mutation — this keeps bookkeeping simple while
// tables are small, which they virtually always are in DMN practice.

function uid(prefix) {
  return prefix + '_' + Math.random().toString(36).slice(2, 9);
}

function textOf(el) {
  if (!el) return '';
  if (typeof el.text === 'string') return el.text;
  return '';
}

function cssEscape(s) {
  const str = String(s || '');
  if (typeof CSS !== 'undefined' && CSS && typeof CSS.escape === 'function') return CSS.escape(str);
  return str.replace(/(["\\])/g, '\\$1');
}

export class DecisionTableEditor {
  constructor(container, { moddle, definitions, onChange, onPromoteInput, onSwitchLogicType } = {}) {
    if (!container) throw new Error('[DecisionTableEditor] container required');
    if (!moddle) throw new Error('[DecisionTableEditor] moddle required');
    this._container = container;
    this._moddle = moddle;
    this._onChange = onChange || (() => {});
    this._onPromoteInput = onPromoteInput || null;
    // Optional handler that swaps the Decision's logic to a different type
    // (Literal, Context, …). Rendered as a chip next to the title so the
    // switch is reachable from the expanded Table view too.
    this._onSwitchLogicType = typeof onSwitchLogicType === 'function' ? onSwitchLogicType : null;
    this._decision = null;
    this._table = null;
    this._issues = [];
    this._definitions = definitions || null;
    this._container.classList.add('epx-dmn-table');

    this._mainEl = document.createElement('div');
    this._mainEl.className = 'epx-dmn-table__main';
    this._scopeEl = document.createElement('aside');
    this._scopeEl.className = 'epx-dmn-table__scope';
    this._container.appendChild(this._mainEl);
    this._container.appendChild(this._scopeEl);
    this._scopePanel = new ScopePanel(this._scopeEl);
  }

  getDecision() {
    return this._decision;
  }

  setDefinitions(definitions) {
    this._definitions = definitions || null;
    if (this._table) this._redraw();
  }

  _getScope() {
    if (!this._decision || !this._definitions) return [];
    return computeScope(this._definitions, this._decision);
  }

  _makeFeelEditable(initialValue, placeholder, commit) {
    return makeFeelEditable(
      initialValue,
      placeholder,
      commit,
      () => this._getScope(),
      'epx-dmn-table__editable',
    );
  }

  setIssues(issues) {
    this._issues = Array.isArray(issues) ? issues : [];
    if (this._table) this._redraw();
  }

  /**
   * Paint simulation results on top of the current table without redrawing.
   *   { matched: [ruleId], conflict: [ruleId], error: {ruleId, colIdx, kind} }
   * Pass null/undefined to clear. Classes are toggled on <tr data-rule-id>
   * and optionally on <td> via data-kind/data-col-idx.
   */
  setSimulationHighlights(payload) {
    this._simHighlights = payload || null;
    this._applySimulationHighlights();
  }

  clearSimulationHighlights() {
    this._simHighlights = null;
    this._applySimulationHighlights();
  }

  _applySimulationHighlights() {
    if (!this._container) return;
    const rows = this._container.querySelectorAll('tr[data-rule-id], tr[data-rule-idx]');
    rows.forEach((tr) => {
      tr.classList.remove('epx-dmn-table__row--matched', 'epx-dmn-table__row--conflict', 'epx-dmn-table__row--error');
      tr.querySelectorAll('.epx-dmn-table__cell--error').forEach((td) => {
        td.classList.remove('epx-dmn-table__cell--error');
      });
    });
    const p = this._simHighlights;
    if (!p) return;

    const findRow = (ruleRef) => {
      if (!ruleRef) return null;
      // Accept either id or index-like numeric.
      const byId = this._container.querySelector(`tr[data-rule-id="${cssEscape(ruleRef)}"]`);
      if (byId) return byId;
      if (typeof ruleRef === 'number' || /^\d+$/.test(String(ruleRef))) {
        return this._container.querySelector(`tr[data-rule-idx="${String(ruleRef)}"]`);
      }
      return null;
    };

    (p.matched || []).forEach((rid) => {
      const tr = findRow(rid);
      if (tr) tr.classList.add('epx-dmn-table__row--matched');
    });
    (p.conflict || []).forEach((rid) => {
      const tr = findRow(rid);
      if (tr) tr.classList.add('epx-dmn-table__row--conflict');
    });
    if (p.error) {
      const tr = findRow(p.error.ruleId);
      if (tr) {
        tr.classList.add('epx-dmn-table__row--error');
        if (p.error.kind && typeof p.error.colIdx === 'number') {
          const sel = `td[data-kind="${p.error.kind}"][data-col-idx="${p.error.colIdx}"]`;
          const td = tr.querySelector(sel);
          if (td) td.classList.add('epx-dmn-table__cell--error');
        }
      }
    }
  }

  /**
   * Scroll a specific rule row (and optionally a cell) into view and briefly
   * pulse it. Returns true if the row was found.
   */
  revealRule(ruleRef, cellKind, colIdx) {
    if (!this._container) return false;
    const byId = this._container.querySelector(`tr[data-rule-id="${cssEscape(ruleRef || '')}"]`);
    const tr = byId || (typeof ruleRef === 'number' || /^\d+$/.test(String(ruleRef))
      ? this._container.querySelector(`tr[data-rule-idx="${String(ruleRef)}"]`)
      : null);
    if (!tr) return false;
    tr.scrollIntoView({ behavior: 'smooth', block: 'center' });
    tr.classList.add('epx-dmn-table__row--pulse');
    setTimeout(() => tr.classList.remove('epx-dmn-table__row--pulse'), 900);
    if (cellKind && typeof colIdx === 'number') {
      const td = tr.querySelector(`td[data-kind="${cellKind}"][data-col-idx="${colIdx}"]`);
      if (td) {
        td.classList.add('epx-dmn-table__cell--pulse');
        setTimeout(() => td.classList.remove('epx-dmn-table__cell--pulse'), 900);
      }
    }
    return true;
  }

  _issuesForInput(inputClause) {
    if (!this._issues || !this._issues.length || !inputClause) return [];
    return this._issues.filter(
      (i) => i.elementId === inputClause.id && i.ruleId === 'input-not-connected'
    );
  }

  render({ decision, decisionTable } = {}) {
    this._decision = decision || null;
    this._table = decisionTable || (decision && decision.decisionLogic) || null;
    this._redraw();
  }

  clear() {
    this._decision = null;
    this._table = null;
    this._mainEl.innerHTML = '';
    if (this._scopePanel) this._scopePanel.setScope([]);
  }

  destroy() {
    if (this._scopePanel) {
      this._scopePanel.destroy();
      this._scopePanel = null;
    }
    this._decision = null;
    this._table = null;
    this._container.innerHTML = '';
    this._container.classList.remove('epx-dmn-table');
  }

  getDecisionTable() {
    return this._table;
  }

  // ── Internal: DOM build ─────────────────────────────────────────────────

  _redraw() {
    this._mainEl.innerHTML = '';
    if (this._scopePanel) this._scopePanel.setScope(this._getScope());
    const table = this._table;
    if (!table) {
      const empty = document.createElement('div');
      empty.className = 'epx-dmn-table__empty';
      empty.textContent = 'No Decision Table to display. Pick a Decision with tabular logic.';
      this._mainEl.appendChild(empty);
      return;
    }

    const inputs = table.input || [];
    const outputs = table.output || [];
    const rules = table.rule || [];

    const root = document.createElement('div');
    root.className = 'epx-dmn-table__scroll';

    const header = document.createElement('div');
    header.className = 'epx-dmn-table__header';
    const name = document.createElement('div');
    name.className = 'epx-dmn-table__title';
    name.textContent = (this._decision && this._decision.name) || table.id || 'Decision Table';
    header.appendChild(name);
    if (this._onSwitchLogicType && this._decision
        && this._decision.$type === 'dmn:Decision') {
      header.appendChild(this._buildLogicSwitcherChip());
    }
    root.appendChild(header);

    const tableEl = document.createElement('table');
    tableEl.className = 'epx-dmn-table__grid';

    // ── Header row 1 — labels + typeRef / values chips ────────────────────
    const thead = document.createElement('thead');
    const labelRow = document.createElement('tr');

    const hpTh = document.createElement('th');
    hpTh.className = 'epx-dmn-table__hitpolicy-cell';
    hpTh.rowSpan = 2;
    const hp = document.createElement('button');
    hp.type = 'button';
    hp.className = 'epx-dmn-table__hitpolicy';
    const policyMeta = getHitPolicy(table.hitPolicy);
    hp.title = policyMeta.label + (table.aggregation ? ' · ' + table.aggregation : '');
    const letterSpan = document.createElement('span');
    letterSpan.className = 'epx-dmn-table__hitpolicy-letter';
    letterSpan.textContent = getHitPolicyLetter(table.hitPolicy, table.aggregation);
    const chevSpan = document.createElement('span');
    chevSpan.className = 'epx-dmn-table__hitpolicy-chev';
    chevSpan.textContent = '▾';
    chevSpan.setAttribute('aria-hidden', 'true');
    hp.appendChild(letterSpan);
    hp.appendChild(chevSpan);
    hp.addEventListener('click', (e) => this._openHitPolicyMenu(e));
    hpTh.appendChild(hp);
    labelRow.appendChild(hpTh);

    inputs.forEach((inp, i) => {
      const th = document.createElement('th');
      th.className = 'epx-dmn-table__col-input epx-dmn-table__label';
      const labelEl = this._makeEditable(
        inp.label || '', 'Input ' + (i + 1),
        (v) => { inp.label = v || undefined; this._changed(); }
      );
      const linked = this._findInputDataByExpr(inp);
      const currentType = linked
        ? (linked.variable && linked.variable.typeRef) || 'string'
        : this._inputTypeRef(inp);
      const typeChip = this._makeTypeChip(
        currentType,
        (newType) => {
          if (linked) {
            if (!linked.variable) {
              linked.variable = this._moddle.create('dmn:InformationItem', {
                id: linked.id + '_var',
                name: sanitizeFeelIdent(linked.name) || linked.name,
                typeRef: newType,
              });
              linked.variable.$parent = linked;
            } else {
              linked.variable.typeRef = newType;
            }
            this._changed();
            this._redraw();
          } else {
            this._setInputTypeRef(inp, newType);
          }
        }
      );
      const valuesChip = this._makeValuesChip(
        'Input values',
        textOf(inp.inputValues),
        'e.g. "red","amber","green"  or  [1..10]',
        (newText) => this._setInputValues(inp, newText)
      );
      th.appendChild(this._makeColumnHeader({
        kind: 'input',
        labelEl,
        typeChip,
        valuesChip,
        onRemove: () => this._removeInputColumn(i),
      }));
      labelRow.appendChild(th);
    });

    const addInputTh = document.createElement('th');
    addInputTh.className = 'epx-dmn-table__col-add';
    addInputTh.rowSpan = 2;
    addInputTh.title = 'Add input column';
    addInputTh.textContent = '+';
    addInputTh.addEventListener('click', () => this._addInputColumn());
    labelRow.appendChild(addInputTh);

    outputs.forEach((out, i) => {
      const th = document.createElement('th');
      th.className = 'epx-dmn-table__col-output epx-dmn-table__label';
      const labelEl = this._makeEditable(
        out.label || out.name || '', 'Output ' + (i + 1),
        (v) => { out.label = v || undefined; this._changed(); }
      );
      const typeChip = this._makeTypeChip(
        out.typeRef || 'string',
        (newType) => { out.typeRef = newType; this._changed(); this._redraw(); }
      );
      const valuesChip = this._makeValuesChip(
        'Output values (ranks for PRIORITY / OUTPUT ORDER)',
        textOf(out.outputValues),
        'e.g. "gold","silver","bronze"',
        (newText) => this._setOutputValues(out, newText)
      );
      const defaultChip = this._makeDefaultChip(out);
      th.appendChild(this._makeColumnHeader({
        kind: 'output',
        labelEl,
        typeChip,
        valuesChip,
        defaultChip,
        onRemove: () => this._removeOutputColumn(i),
      }));
      labelRow.appendChild(th);
    });

    const addOutputTh = document.createElement('th');
    addOutputTh.className = 'epx-dmn-table__col-add';
    addOutputTh.rowSpan = 2;
    addOutputTh.title = 'Add output column';
    addOutputTh.textContent = '+';
    addOutputTh.addEventListener('click', () => this._addOutputColumn());
    labelRow.appendChild(addOutputTh);

    const annotHeader = document.createElement('th');
    annotHeader.className = 'epx-dmn-table__annotation-header';
    annotHeader.rowSpan = 2;
    annotHeader.textContent = 'Annotation';
    labelRow.appendChild(annotHeader);

    thead.appendChild(labelRow);

    // Header row 2 — input expressions / output names
    const exprRow = document.createElement('tr');
    inputs.forEach((inp) => {
      const th = document.createElement('th');
      th.className = 'epx-dmn-table__col-input epx-dmn-table__expr';
      const expr = inp.inputExpression;
      th.appendChild(this._makeFeelEditable(
        textOf(expr),
        'expression',
        (v) => {
          if (!inp.inputExpression) {
            inp.inputExpression = this._moddle.create('dmn:LiteralExpression', {
              id: uid('InputExpr'),
              text: v,
              typeRef: 'string',
            });
          } else {
            inp.inputExpression.text = v;
          }
          this._changed();
        }
      ));

      const issues = this._issuesForInput(inp);
      if (issues.length) {
        th.classList.add('epx-dmn-table__col-issue');
        th.title = issues.map((i) => i.message).join('\n');
        if (this._onPromoteInput) {
          const promoteBtn = document.createElement('button');
          promoteBtn.type = 'button';
          promoteBtn.className = 'epx-dmn-table__promote-btn';
          promoteBtn.title = 'Crear InputData en el dmn y conectarlo a esta decisión';
          promoteBtn.textContent = '＋ dmn';
          promoteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            try { this._onPromoteInput({ inputClause: inp, decision: this._decision, issues }); }
            catch (err) { console.error('[DecisionTableEditor] promote error', err); }
          });
          th.appendChild(promoteBtn);
        }
      }

      exprRow.appendChild(th);
    });
    outputs.forEach((out) => {
      const th = document.createElement('th');
      th.className = 'epx-dmn-table__col-output epx-dmn-table__expr';
      th.appendChild(this._makeEditable(out.name || '', 'name', (v) => { out.name = v || undefined; this._changed(); }));
      exprRow.appendChild(th);
    });
    thead.appendChild(exprRow);
    tableEl.appendChild(thead);

    // ── Rule rows ────────────────────────────────────────────────────────
    const tbody = document.createElement('tbody');
    rules.forEach((rule, rIdx) => {
      const tr = document.createElement('tr');
      tr.dataset.ruleIdx = String(rIdx);
      if (rule.id) tr.dataset.ruleId = rule.id;

      const idx = document.createElement('td');
      idx.className = 'epx-dmn-table__index';
      idx.textContent = String(rIdx + 1);
      idx.title = 'Click to remove rule';
      idx.addEventListener('click', () => this._removeRule(rIdx));
      tr.appendChild(idx);

      inputs.forEach((inp, cIdx) => {
        const entry = (rule.inputEntry || [])[cIdx];
        const td = document.createElement('td');
        td.className = 'epx-dmn-table__cell epx-dmn-table__cell-input';
        td.dataset.kind = 'inputEntry';
        td.dataset.colIdx = String(cIdx);
        if (inp && inp.id) td.dataset.inputId = inp.id;
        td.appendChild(this._makeFeelEditable(
          textOf(entry),
          '-',
          (v) => this._setInputEntry(rule, cIdx, v)
        ));
        tr.appendChild(td);
      });

      // spacer for input-add column
      const spL = document.createElement('td');
      spL.className = 'epx-dmn-table__spacer';
      tr.appendChild(spL);

      outputs.forEach((out, cIdx) => {
        const entry = (rule.outputEntry || [])[cIdx];
        const td = document.createElement('td');
        td.className = 'epx-dmn-table__cell epx-dmn-table__cell-output';
        td.dataset.kind = 'outputEntry';
        td.dataset.colIdx = String(cIdx);
        if (out && out.id) td.dataset.outputId = out.id;
        td.appendChild(this._makeFeelEditable(
          textOf(entry),
          '',
          (v) => this._setOutputEntry(rule, cIdx, v)
        ));
        tr.appendChild(td);
      });

      // spacer for output-add column
      const spR = document.createElement('td');
      spR.className = 'epx-dmn-table__spacer';
      tr.appendChild(spR);

      const annot = document.createElement('td');
      annot.className = 'epx-dmn-table__cell epx-dmn-table__annotation';
      const annotWrap = document.createElement('span');
      annotWrap.className = 'epx-dmn-table__annot';
      const annotIcon = document.createElement('span');
      annotIcon.className = 'epx-dmn-table__annot-icon';
      annotIcon.setAttribute('aria-hidden', 'true');
      annotIcon.textContent = 'i';
      annotWrap.appendChild(annotIcon);
      annotWrap.appendChild(this._makeEditable(
        rule.description || '',
        '',
        (v) => { rule.description = v || undefined; this._changed(); }
      ));
      annot.appendChild(annotWrap);
      tr.appendChild(annot);

      tbody.appendChild(tr);
    });

    // ── Default-output row — shown only if at least one output has one ───
    if (outputs.some((o) => o.defaultOutputEntry)) {
      const defRow = document.createElement('tr');
      defRow.className = 'epx-dmn-table__default-row';

      const defLabel = document.createElement('td');
      defLabel.className = 'epx-dmn-table__default-label';
      defLabel.colSpan = 1 + inputs.length + 1; // hp + inputs + add-input
      defLabel.textContent = 'default →';
      defRow.appendChild(defLabel);

      outputs.forEach((out) => {
        const td = document.createElement('td');
        td.className = 'epx-dmn-table__cell epx-dmn-table__cell-output';
        td.appendChild(this._makeFeelEditable(
          textOf(out.defaultOutputEntry),
          'no match → default',
          (v) => this._setDefaultOutputEntry(out, v)
        ));
        defRow.appendChild(td);
      });

      const spR = document.createElement('td');
      spR.className = 'epx-dmn-table__spacer';
      defRow.appendChild(spR);

      const pad = document.createElement('td');
      pad.className = 'epx-dmn-table__annotation';
      defRow.appendChild(pad);

      tbody.appendChild(defRow);
    }

    // Add rule row
    const addTr = document.createElement('tr');
    const addTd = document.createElement('td');
    addTd.className = 'epx-dmn-table__add-rule';
    addTd.colSpan = 1 + inputs.length + 1 + outputs.length + 1 + 1; // hp + inputs + input-add + outputs + output-add + annotation
    addTd.textContent = '+ Add rule';
    addTd.addEventListener('click', () => this._addRule());
    addTr.appendChild(addTd);
    tbody.appendChild(addTr);

    tableEl.appendChild(tbody);
    root.appendChild(tableEl);
    this._mainEl.appendChild(root);

    if (this._simHighlights) this._applySimulationHighlights();
  }

  // ── Internal: typeRef / values / default helpers ───────────────────────

  _inputTypeRef(inputClause) {
    const expr = inputClause && inputClause.inputExpression;
    return (expr && expr.typeRef) || 'string';
  }

  // Si la expresión de la columna es un identificador FEEL simple que coincide
  // con el `name` de un `dmn:InputData` del DRD, devuélvelo. Es el "vínculo"
  // que activa el comportamiento write-through.
  _findInputDataByExpr(inputClause) {
    if (!this._definitions) return null;
    const text = inputClause && inputClause.inputExpression && inputClause.inputExpression.text;
    if (!text) return null;
    const trimmed = String(text).trim();
    if (!/^[A-Za-z_][\w]*$/.test(trimmed)) return null;
    return (this._definitions.drgElement || []).find(
      (e) => e.$type === 'dmn:InputData' && e.name === trimmed
    ) || null;
  }

  _setInputTypeRef(inputClause, typeRef) {
    if (!inputClause.inputExpression) {
      inputClause.inputExpression = this._moddle.create('dmn:LiteralExpression', {
        id: uid('InputExpr'), text: '', typeRef,
      });
      inputClause.inputExpression.$parent = inputClause;
    } else {
      inputClause.inputExpression.typeRef = typeRef;
    }
    this._changed();
    this._redraw();
  }

  _setInputValues(inputClause, text) {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      inputClause.inputValues = undefined;
    } else if (!inputClause.inputValues) {
      inputClause.inputValues = this._moddle.create('dmn:UnaryTests', { id: uid('InputValues'), text: trimmed });
      inputClause.inputValues.$parent = inputClause;
    } else {
      inputClause.inputValues.text = trimmed;
    }
    this._changed();
    this._redraw();
  }

  _setOutputValues(outputClause, text) {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      outputClause.outputValues = undefined;
    } else if (!outputClause.outputValues) {
      outputClause.outputValues = this._moddle.create('dmn:UnaryTests', { id: uid('OutputValues'), text: trimmed });
      outputClause.outputValues.$parent = outputClause;
    } else {
      outputClause.outputValues.text = trimmed;
    }
    this._changed();
    this._redraw();
  }

  _setDefaultOutputEntry(outputClause, text) {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      outputClause.defaultOutputEntry = undefined;
    } else if (!outputClause.defaultOutputEntry) {
      outputClause.defaultOutputEntry = this._moddle.create('dmn:LiteralExpression', {
        id: uid('DefaultOutput'), text: trimmed,
      });
      outputClause.defaultOutputEntry.$parent = outputClause;
    } else {
      outputClause.defaultOutputEntry.text = trimmed;
    }
    this._changed();
    this._redraw();
  }

  _makeTypeChip(currentType, onPick) {
    const chip = document.createElement('span');
    chip.className = 'epx-dmn-table__type-chip';
    chip.title = 'Data type (typeRef) — click to change';
    chip.textContent = ': ' + (currentType || 'string');
    chip.addEventListener('click', (evt) => {
      evt.stopPropagation();
      this._openTypeMenu(evt, currentType, onPick);
    });
    return chip;
  }

  _makeValuesChip(label, currentText, placeholder, onCommit) {
    const chip = document.createElement('span');
    chip.className = 'epx-dmn-table__values-chip';
    if (currentText) chip.classList.add('epx-dmn-table__values-chip--set');
    chip.title = label + ' — click to edit';
    chip.textContent = currentText ? '⚑ values' : '＋ values';
    chip.addEventListener('click', (evt) => {
      evt.stopPropagation();
      this._openTextPopover(evt, label, currentText, placeholder, onCommit);
    });
    return chip;
  }

  _makeDefaultChip(outputClause) {
    const chip = document.createElement('span');
    chip.className = 'epx-dmn-table__values-chip';
    const current = textOf(outputClause.defaultOutputEntry);
    if (current) chip.classList.add('epx-dmn-table__values-chip--set');
    chip.title = 'Default output — returned when no rule matches (UNIQUE/ANY/FIRST/PRIORITY)';
    chip.textContent = current ? '⚑ default' : '＋ default';
    chip.addEventListener('click', (evt) => {
      evt.stopPropagation();
      this._openTextPopover(
        evt,
        'Default output',
        current,
        'FEEL expression, e.g. "unknown"',
        (v) => this._setDefaultOutputEntry(outputClause, v)
      );
    });
    return chip;
  }

  _makeColumnHeader({ kind, labelEl, typeChip, valuesChip, defaultChip, onRemove }) {
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-table__col-header epx-dmn-table__col-header--' + kind;

    const nameRow = document.createElement('div');
    nameRow.className = 'epx-dmn-table__col-name-row';
    nameRow.appendChild(labelEl);
    if (onRemove) {
      const actions = document.createElement('div');
      actions.className = 'epx-dmn-table__col-actions';
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'epx-dmn-table__col-remove';
      rm.title = 'Remove column';
      rm.textContent = '×';
      rm.addEventListener('click', onRemove);
      actions.appendChild(rm);
      nameRow.appendChild(actions);
    }
    wrap.appendChild(nameRow);

    const metaRow = document.createElement('div');
    metaRow.className = 'epx-dmn-table__col-meta-row';
    if (typeChip)    metaRow.appendChild(typeChip);
    if (valuesChip)  metaRow.appendChild(valuesChip);
    if (defaultChip) metaRow.appendChild(defaultChip);
    wrap.appendChild(metaRow);

    return wrap;
  }

  // ── Internal: popovers ─────────────────────────────────────────────────

  _openTypeMenu(evt, currentType, onPick) {
    this._closePopovers();
    const menu = document.createElement('div');
    menu.className = 'epx-dmn-table__popover';
    const rect = evt.currentTarget.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 2) + 'px';

    const title = document.createElement('div');
    title.className = 'epx-dmn-table__popover-title';
    title.textContent = 'Data type';
    menu.appendChild(title);

    FEEL_TYPES.forEach((t) => {
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'epx-dmn-table__popover-item';
      opt.textContent = t;
      if ((currentType || 'string') === t) opt.classList.add('is-active');
      opt.addEventListener('click', () => {
        this._closePopovers();
        onPick(t);
      });
      menu.appendChild(opt);
    });

    document.body.appendChild(menu);
    this._installPopoverDismiss(menu);
  }

  _openTextPopover(evt, title, currentValue, placeholder, onCommit) {
    this._closePopovers();
    const pop = document.createElement('div');
    pop.className = 'epx-dmn-table__popover';
    const rect = evt.currentTarget.getBoundingClientRect();
    pop.style.left = rect.left + 'px';
    pop.style.top = (rect.bottom + 2) + 'px';

    const head = document.createElement('div');
    head.className = 'epx-dmn-table__popover-title';
    head.textContent = title;
    pop.appendChild(head);

    const body = document.createElement('div');
    body.className = 'epx-dmn-table__popover-body';

    const ta = document.createElement('textarea');
    ta.rows = 3;
    ta.value = currentValue || '';
    ta.placeholder = placeholder || '';
    body.appendChild(ta);

    const hint = document.createElement('div');
    hint.className = 'epx-dmn-table__popover-hint';
    hint.textContent = 'Leave empty to clear. Use FEEL literal syntax.';
    body.appendChild(hint);

    const actions = document.createElement('div');
    actions.className = 'epx-dmn-table__popover-actions';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'epx-dmn-table__popover-btn';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => this._closePopovers());
    actions.appendChild(cancel);

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'epx-dmn-table__popover-btn epx-dmn-table__popover-btn--primary';
    save.textContent = 'Save';
    save.addEventListener('click', () => {
      this._closePopovers();
      onCommit(ta.value);
    });
    actions.appendChild(save);

    body.appendChild(actions);
    pop.appendChild(body);

    document.body.appendChild(pop);
    setTimeout(() => ta.focus(), 0);
    this._installPopoverDismiss(pop);
  }

  _closePopovers() {
    document.querySelectorAll('.epx-dmn-table__popover, .epx-dmn-table__hp-menu')
      .forEach((el) => el.remove());
  }

  _installPopoverDismiss(el) {
    const close = (e) => {
      if (!el.contains(e.target)) {
        el.remove();
        document.removeEventListener('mousedown', close);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }

  // ── Internal: editable cells ─────────────────────────────────────────────

  _makeEditable(initialValue, placeholder, commit) {
    const span = document.createElement('span');
    span.className = 'epx-dmn-table__editable';
    span.setAttribute('contenteditable', 'true');
    span.setAttribute('spellcheck', 'false');
    span.textContent = initialValue;
    if (!initialValue) {
      span.classList.add('epx-dmn-table__editable--empty');
      span.dataset.placeholder = placeholder || '';
    }
    span.addEventListener('focus', () => {
      span.classList.remove('epx-dmn-table__editable--empty');
    });
    span.addEventListener('blur', () => {
      const v = (span.textContent || '').trim();
      commit(v);
    });
    span.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        span.blur();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        span.textContent = initialValue;
        span.blur();
      }
    });
    return span;
  }

  // ── Internal: moddle mutations ──────────────────────────────────────────

  _setInputEntry(rule, colIndex, value) {
    if (!rule.inputEntry) rule.inputEntry = [];
    let entry = rule.inputEntry[colIndex];
    if (!entry) {
      entry = this._moddle.create('dmn:UnaryTests', { id: uid('InputEntry'), text: value });
      entry.$parent = rule;
      rule.inputEntry[colIndex] = entry;
    } else {
      entry.text = value;
    }
    this._changed();
  }

  _setOutputEntry(rule, colIndex, value) {
    if (!rule.outputEntry) rule.outputEntry = [];
    let entry = rule.outputEntry[colIndex];
    if (!entry) {
      entry = this._moddle.create('dmn:LiteralExpression', { id: uid('OutputEntry'), text: value });
      entry.$parent = rule;
      rule.outputEntry[colIndex] = entry;
    } else {
      entry.text = value;
    }
    this._changed();
  }

  _addRule() {
    const table = this._table;
    if (!table) return;
    if (!table.rule) table.rule = [];
    const rule = this._moddle.create('dmn:DecisionRule', { id: uid('Rule') });
    rule.$parent = table;
    rule.inputEntry = (table.input || []).map(() =>
      this._moddle.create('dmn:UnaryTests', { id: uid('InputEntry'), text: '-' })
    );
    rule.outputEntry = (table.output || []).map(() =>
      this._moddle.create('dmn:LiteralExpression', { id: uid('OutputEntry'), text: '' })
    );
    rule.inputEntry.forEach((e) => (e.$parent = rule));
    rule.outputEntry.forEach((e) => (e.$parent = rule));
    table.rule.push(rule);
    this._changed();
    this._redraw();
  }

  _removeRule(idx) {
    const table = this._table;
    if (!table || !table.rule || idx < 0 || idx >= table.rule.length) return;
    if (table.rule.length === 1) return; // always keep at least one rule
    table.rule.splice(idx, 1);
    this._changed();
    this._redraw();
  }

  _addInputColumn() {
    const table = this._table;
    if (!table) return;
    if (!table.input) table.input = [];
    const expr = this._moddle.create('dmn:LiteralExpression', {
      id: uid('InputExpr'), text: '', typeRef: 'string',
    });
    const input = this._moddle.create('dmn:InputClause', { id: uid('Input'), inputExpression: expr });
    expr.$parent = input;
    input.$parent = table;
    table.input.push(input);
    (table.rule || []).forEach((rule) => {
      if (!rule.inputEntry) rule.inputEntry = [];
      const entry = this._moddle.create('dmn:UnaryTests', { id: uid('InputEntry'), text: '-' });
      entry.$parent = rule;
      rule.inputEntry.push(entry);
    });
    this._changed();
    this._redraw();
    this._focusLastInputColumn();
  }

  _focusLastInputColumn() {
    const cells = this._container.querySelectorAll('th.epx-dmn-table__col-input.epx-dmn-table__expr');
    if (!cells.length) return;
    const last = cells[cells.length - 1];
    const editable = last.querySelector('[contenteditable="true"], input, textarea');
    if (editable && typeof editable.focus === 'function') editable.focus();
  }

  _removeInputColumn(idx) {
    const table = this._table;
    if (!table || !table.input || idx < 0 || idx >= table.input.length) return;
    table.input.splice(idx, 1);
    (table.rule || []).forEach((rule) => {
      if (rule.inputEntry && rule.inputEntry.length > idx) rule.inputEntry.splice(idx, 1);
    });
    this._changed();
    this._redraw();
  }

  _addOutputColumn() {
    const table = this._table;
    if (!table) return;
    if (!table.output) table.output = [];
    const output = this._moddle.create('dmn:OutputClause', { id: uid('Output'), typeRef: 'string' });
    output.$parent = table;
    table.output.push(output);
    (table.rule || []).forEach((rule) => {
      if (!rule.outputEntry) rule.outputEntry = [];
      const entry = this._moddle.create('dmn:LiteralExpression', { id: uid('OutputEntry'), text: '' });
      entry.$parent = rule;
      rule.outputEntry.push(entry);
    });
    this._changed();
    this._redraw();
  }

  _removeOutputColumn(idx) {
    const table = this._table;
    if (!table || !table.output || idx < 0 || idx >= table.output.length) return;
    if (table.output.length === 1) return; // always keep at least one output
    table.output.splice(idx, 1);
    (table.rule || []).forEach((rule) => {
      if (rule.outputEntry && rule.outputEntry.length > idx) rule.outputEntry.splice(idx, 1);
    });
    this._changed();
    this._redraw();
  }

  _buildLogicSwitcherChip() {
    const currentType = 'dmn:DecisionTable';
    const label = (LOGIC_TYPES.find((t) => t.type === currentType) || {}).label || 'Decision Table';
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'epx-dmn-table__logic-chip';
    chip.title = 'Cambiar el tipo de lógica de la decisión';
    chip.textContent = label + '  ▾';
    chip.addEventListener('click', (evt) => {
      evt.stopPropagation();
      this._openLogicSwitcherMenu(chip, currentType);
    });
    return chip;
  }

  _openLogicSwitcherMenu(anchor, currentType) {
    document.querySelectorAll('.epx-dmn-table__logic-menu').forEach((m) => m.remove());
    const menu = document.createElement('div');
    menu.className = 'epx-dmn-table__logic-menu';
    const rect = anchor.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 4) + 'px';
    LOGIC_TYPES.forEach((opt) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'epx-dmn-table__logic-item';
      if (opt.type === currentType) b.classList.add('is-active');
      b.textContent = opt.label;
      b.addEventListener('click', () => {
        menu.remove();
        if (opt.type !== currentType && this._onSwitchLogicType) {
          this._onSwitchLogicType(opt.type);
        }
      });
      menu.appendChild(b);
    });
    document.body.appendChild(menu);
    const close = (e) => {
      if (!menu.contains(e.target) && e.target !== anchor) {
        menu.remove();
        document.removeEventListener('mousedown', close);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }

  _openHitPolicyMenu(evt) {
    const table = this._table;
    if (!table) return;
    this._closePopovers();
    const menu = document.createElement('div');
    menu.className = 'epx-dmn-table__hp-menu';
    const rect = evt.currentTarget.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 2) + 'px';
    HIT_POLICIES.forEach((hp) => {
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'epx-dmn-table__hp-item';
      const line = document.createElement('span');
      line.textContent = `${hp.letter} — ${hp.label}`;
      opt.appendChild(line);
      const desc = document.createElement('span');
      desc.className = 'epx-dmn-table__hp-item-desc';
      desc.textContent = hp.description;
      opt.appendChild(desc);
      if ((table.hitPolicy || 'UNIQUE') === hp.id) opt.classList.add('is-active');
      opt.addEventListener('click', () => {
        table.hitPolicy = hp.id;
        if (hp.group !== 'multi') table.aggregation = undefined;
        menu.remove();
        this._changed();
        this._redraw();
      });
      menu.appendChild(opt);
    });

    if ((table.hitPolicy || 'UNIQUE') === 'COLLECT') {
      const sep = document.createElement('div');
      sep.className = 'epx-dmn-table__hp-sep';
      menu.appendChild(sep);
      AGGREGATIONS.forEach((agg) => {
        const opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'epx-dmn-table__hp-item';
        const line = document.createElement('span');
        line.textContent = `${agg.letter || '·'} — ${agg.label}`;
        opt.appendChild(line);
        const desc = document.createElement('span');
        desc.className = 'epx-dmn-table__hp-item-desc';
        desc.textContent = agg.description;
        opt.appendChild(desc);
        if ((table.aggregation || null) === agg.id) opt.classList.add('is-active');
        opt.addEventListener('click', () => {
          table.aggregation = agg.id || undefined;
          menu.remove();
          this._changed();
          this._redraw();
        });
        menu.appendChild(opt);
      });
    }

    document.body.appendChild(menu);
    this._installPopoverDismiss(menu);
  }

  _changed() {
    try { this._onChange({ decisionTable: this._table, decision: this._decision }); }
    catch (err) { console.error('[DecisionTableEditor] onChange error', err); }
  }
}
